import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Popover,
  Chip,
  Button,
  useTheme
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TranslateIcon from '@mui/icons-material/Translate';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReplayIcon from '@mui/icons-material/Replay';

// Grammar correction tooltip component
const GrammarHelp = ({ text, correction, explanation, anchorEl, onClose }) => {
  const open = Boolean(anchorEl);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: { p: 2, maxWidth: 300, boxShadow: 3 }
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Correction
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            display: 'inline-block',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            mb: 0.5
          }}
        >
          {correction}
        </Typography>
      </Box>

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Explanation (in English)
      </Typography>
      <Typography variant="body2">
        {explanation}
      </Typography>
    </Popover>
  );
};

// Message bubble component
const MessageBubble = ({ message, onPlayAudio, onResend, onUseHint  }) => {
  const theme = useTheme();
  const [showTranslation, setShowTranslation] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [grammarAnchorEl, setGrammarAnchorEl] = useState(null);
  const [selectedCorrection, setSelectedCorrection] = useState(null);

  const {
    sender,
    content,
    translation,
    timestamp,
    corrections = [],
    hints = [],
    failed = false
  } = message;

  // Format timestamp
  const formattedTime = new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Toggle translation visibility
  const toggleTranslation = () => {
    setShowTranslation(prev => !prev);
  };

  // Toggle hints visibility
  const toggleHints = () => {
    setShowHints(prev => !prev);
  };

  // Show grammar correction popup
  const handleCorrectionClick = (event, correction) => {
    setGrammarAnchorEl(event.currentTarget);
    setSelectedCorrection(correction);
  };

  // Close grammar correction popup
  const handleCorrectionClose = () => {
    setGrammarAnchorEl(null);
    setSelectedCorrection(null);
  };

  // Play audio for the message
  const handlePlayAudio = () => {
    if (onPlayAudio) {
      onPlayAudio(content);
    }
  };

  // Handle resend for failed messages
  const handleResend = () => {
    if (onResend) {
      onResend(message);
    }
  };

  // Determine if message is from system or user
  const isSystem = sender === 'system';

  // Generate content with highlighted corrections - using subtle styling
  const renderMessageContent = () => {
    if (!corrections || corrections.length === 0) {
      return <Typography variant="body1">{content}</Typography>;
    }

    // Simple text highlighting for corrections
    let parts = [];
    let lastIndex = 0;

    corrections.forEach((correction, index) => {
      const { startIndex, endIndex } = correction;

      // Text before correction
      if (startIndex > lastIndex) {
        parts.push(
          <Typography key={`text-${index}-1`} component="span" variant="body1">
            {content.substring(lastIndex, startIndex)}
          </Typography>
        );
      }

      // Highlighted correction - just using dotted underline as requested
      parts.push(
        <Typography
          key={`correction-${index}`}
          component="span"
          variant="body1"
          sx={{
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textDecorationColor: theme.palette.primary.main,
            textUnderlineOffset: '3px',
            cursor: 'pointer',
          }}
          onClick={(e) => handleCorrectionClick(e, correction)}
        >
          {content.substring(startIndex, endIndex)}
        </Typography>
      );

      lastIndex = endIndex;
    });

    // Text after last correction
    if (lastIndex < content.length) {
      parts.push(
        <Typography key="text-last" component="span" variant="body1">
          {content.substring(lastIndex)}
        </Typography>
      );
    }

    return <Box>{parts}</Box>;
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: isSystem ? 'flex-start' : 'flex-end',
      mb: 2,
      position: 'relative'
    }}>
      <Paper sx={{
        p: 2,
        maxWidth: '85%',
        borderRadius: isSystem ? '4px 18px 18px 18px' : '18px 4px 18px 18px',
        bgcolor: isSystem ? 'background.paper' : 'primary.main',
        color: isSystem ? 'text.primary' : 'primary.contrastText',
        boxShadow: 2
      }}>
        {/* Message content with possible grammar highlighting */}
        {renderMessageContent()}

        {/* Failed message UI with resend button */}
        {failed && (
          <Box sx={{
            mt: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                fontWeight: 'medium'
              }}
            >
              Failed to send
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleResend}
              sx={{
                minHeight: 0,
                py: 0.5,
                px: 1,
                borderRadius: 5,
                fontSize: '0.75rem'
              }}
            >
              Resend
            </Button>
          </Box>
        )}

        {/* Translation section */}
        {translation && (
          <Collapse in={showTranslation}>
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: isSystem ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.9
                }}
              >
                {translation}
              </Typography>
            </Box>
          </Collapse>
        )}

        {/* Hints section */}
        {hints && hints.length > 0 && (
          <Collapse in={showHints}>
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  color: isSystem ? 'text.primary' : 'primary.contrastText',
                }}
              >
                Suggested Phrases:
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {hints.map((hint, i) => (
                  <Chip
                    key={i}
                    label={hint}
                    size="small"
                    onClick={() => onUseHint && onUseHint(hint)}
                    sx={{
                      bgcolor: isSystem ? 'primary.light' : 'primary.dark',
                      color: 'primary.contrastText',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: isSystem ? 'primary.main' : 'primary.light',
                        boxShadow: 1
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Collapse>
        )}

        {/* Message actions */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mt: 1.5,
          gap: 0.5
        }}>
          <Typography
            variant="caption"
            sx={{
              color: isSystem ? 'text.secondary' : 'primary.contrastText',
              opacity: 0.8,
              mr: 0.5
            }}
          >
            {formattedTime}
          </Typography>

          {/* Keeping the hints button in message bubbles as requested */}
          {hints && hints.length > 0 && (
            <Tooltip title={showHints ? "Hide suggestions" : "Show suggestions"}>
              <IconButton
                size="small"
                onClick={toggleHints}
                sx={{
                  color: showHints
                    ? (isSystem ? 'primary.main' : 'primary.contrastText')
                    : (isSystem ? 'text.secondary' : 'primary.contrastText'),
                  opacity: showHints ? 1 : 0.7
                }}
              >
                <AutoFixHighIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {translation && (
            <Tooltip title={showTranslation ? "Hide translation" : "Show translation"}>
              <IconButton
                size="small"
                onClick={toggleTranslation}
                sx={{
                  color: showTranslation
                    ? (isSystem ? 'primary.main' : 'primary.contrastText')
                    : (isSystem ? 'text.secondary' : 'primary.contrastText'),
                  opacity: showTranslation ? 1 : 0.7
                }}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isSystem && (
            <Tooltip title="Listen">
              <IconButton
                size="small"
                onClick={handlePlayAudio}
                sx={{
                  color: 'text.secondary',
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <VolumeUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* Grammar correction popover */}
      {selectedCorrection && (
        <GrammarHelp
          text={selectedCorrection.original}
          correction={selectedCorrection.correction}
          explanation={selectedCorrection.explanation}
          anchorEl={grammarAnchorEl}
          onClose={handleCorrectionClose}
        />
      )}
    </Box>
  );
};

// Typing indicator animation
const TypingIndicator = () => {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-start',
      mb: 2
    }}>
      <Paper
        sx={{
          p: 2,
          borderRadius: '4px 18px 18px 18px',
          bgcolor: 'background.paper',
          width: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <Box
          className="typing-dot"
          sx={{
            height: 8,
            width: 8,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            animation: 'typingAnimation 1.4s infinite ease-in-out both',
            animationDelay: '0s'
          }}
        />
        <Box
          className="typing-dot"
          sx={{
            height: 8,
            width: 8,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            animation: 'typingAnimation 1.4s infinite ease-in-out both',
            animationDelay: '0.2s'
          }}
        />
        <Box
          className="typing-dot"
          sx={{
            height: 8,
            width: 8,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            animation: 'typingAnimation 1.4s infinite ease-in-out both',
            animationDelay: '0.4s'
          }}
        />
        <style jsx global>{`
          @keyframes typingAnimation {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.6; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </Paper>
    </Box>
  );
};

// Main message container
const MessageContainer = ({ messages, userLanguage, onPlayAudio, onResend, isTyping = false, onUseHint }) => {
  return (
    <Box>
      {messages.map((message, index) => (
        <MessageBubble
          key={message._id || index}
          message={message}
          onPlayAudio={onPlayAudio}
          onResend={onResend}
          onUseHint={onUseHint}
        />
      ))}

      {isTyping && <TypingIndicator />}
    </Box>
  );
};

export default MessageContainer;